import React, { useMemo, useState } from 'react';
import {
  useTable,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type ExpandedState,
} from '@tanstack/react-table';
import PaginatedTable from './PaginatedTable';
import { appTableFeatures, type AppTableFeatures } from './tableFeatures';
import type { Event } from '../models/Event';
import type { AlpacaEvent } from '../models/AlpacaEvent';

import { format } from 'date-fns';
import MobileList from './MobileList';
import { FaCaretDown, FaCaretRight, FaCircle } from 'react-icons/fa';
import useMediaQuery from '../helpers/useMediaQuery';
import { Link } from 'react-router';

export default function EventTable({
  events,
  showActions = false,
}: {
  events: Event[];
  showActions?: boolean;
}) {
  const matches = useMediaQuery('screen and (max-width: 640px)');

  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = React.useState('');

  const tableData = useMemo(() => events, [events]);
  const columnData: ColumnDef<AppTableFeatures, Event>[] = useMemo(
    () => [
      {
        header: 'Date',
        accessorFn: (d) =>
          d.date ? format(new Date(d.date ?? ''), 'MM/dd/yyyy') : '',
        sortFn: (a, b, _, desc = true) => {
          const aDate = new Date(a.original.date ?? '');
          const bDate = new Date(b.original.date ?? '');
          if (desc) {
            return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
          }
          return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
        },
        filterFn: 'includesString',
        size: 150,
        cell: ({ row }) => (
          <Link
            className='font-bold hover:opacity-70 dark:text-gray-300'
            to={`/events/${row.original.eventId}`}
          >
            {format(new Date(row.original.date ?? ''), 'MM/dd/yyyy')}
          </Link>
        ),
      },
      {
        header: 'Event Name',
        accessorFn: (d) =>
          d.eventTypeId == 7 ? d.otherEventName : d.eventType?.eventTypeName,
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },

      {
        header: 'Medication',
        accessorKey: 'medication',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
      },
      {
        header: 'Contact',
        accessorFn: (d) => d.contact?.name ?? '',
        sortFn: 'datetime',
        filterFn: 'equals',
        size: 175,
        cell: ({ row }) => (
          <Link
            className='font-bold hover:opacity-70 dark:text-gray-300'
            to={`/contacts/${row.original.contactId}`}
          >
            {row.original.contact?.name ?? ''}
          </Link>
        ),
      },
    ],
    [],
  );

  const expanderColumn: ColumnDef<AppTableFeatures, Event> = useMemo(
    () => ({
      header: '',
      id: 'expander',
      enableSorting: false,
      enableColumnFilter: false,
      size: 40,
      cell: ({ row }) => (
        <div>
          {row.getCanExpand() ? (
            <button title='Expand' onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <FaCaretDown /> : <FaCaretRight />}
            </button>
          ) : (
            <FaCircle />
          )}
        </div>
      ),
    }),
    [],
  );

  const columns = useMemo(
    () => (showActions ? [...columnData] : [expanderColumn, ...columnData]),
    [columnData, expanderColumn, showActions],
  );

  // alpaca events
  const columns2: ColumnDef<AppTableFeatures, AlpacaEvent>[] = useMemo(
    () => [
      {
        header: 'Alpaca',
        accessorKey: 'alpaca.name',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        size: 350,
        cell: ({ row }) => (
          <Link
            prefetch='viewport'
            to={`/alpacas/${row.original.alpaca?.alpacaId}`}
            className='font-bold hover:opacity-70 dark:text-gray-300'
          >
            {row.original.alpaca?.name}
          </Link>
        ),
      },
      {
        header: 'Note',
        accessorKey: 'note',
        sortFn: 'alphanumeric',
        filterFn: 'includesString',
        cell: ({ row }) => (
          <div className='whitespace-pre-line'>{row.original.note}</div>
        ),
      },
    ],
    [],
  );

  const table = useTable({
    features: appTableFeatures,
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      expanded,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: events.length > 9 ? 10 : events.length,
      },
    },
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    paginateExpandedRows: false,
    getRowCanExpand: (row) =>
      row.original.alpacasForEvent != null &&
      row.original.alpacasForEvent.length > 0,
  });

  if (matches) {
    return (
      <MobileList
        table={table}
        columnResizeMode='onChange'
        showFilters={true}
        showPagination={events.length > 9}
        columns2={showActions ? undefined : columns2}
        subRowProp={showActions ? undefined : 'alpacasForEvent'}
      />
    );
  }
  return (
    <PaginatedTable
      table={table}
      columnResizeMode='onChange'
      showFilters={true}
      showPagination={events.length > 9}
      columns2={showActions ? undefined : columns2}
      subRowProp={showActions ? undefined : 'alpacasForEvent'}
    />
  );
}
